import { useState, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SttButton } from '../../../sporttia/buttons/SttButton';
import { usePOSConfig } from '../../../lib/hooks';
import { AppContext, SttList } from '../../../sporttia/all';
import ConfigurationPosDialog from './ConfigurationPOSDialog';
import { requestAdHoc } from '../../../lib/utils';
import { useCanModifyConfiguration } from './useCanModifyConfiguration';

type Device = {
    ip: string;
};

type POSInitResponse = {
    resultCode: string;
    [key: string]: unknown;
};

/**
 * Pantalla de configuración de POS (datáfono físico, para distinguir de TPV o 'datáfono virtual')
 */
export default function ConfigurationPos(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const canModify = useCanModifyConfiguration();

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    // devices es un array por si acaso, aunque ahora mismo solo guardamos un valor
    const [devices, setDevices] = useState<Device[] | null>(null);
    const [testing, setTesting] = useState<boolean>(false);

    // load config builder object
    const setupPOSConfig = usePOSConfig('PAYCOMET');

    // Load existing POS ip
    useEffect(() => {
        const ip = localStorage.getItem('CM5DeviceAddress');
        if (ip) {
            setDevices([{ ip }]);
        }
    }, []);

    // Al modificar devices, se guarda el valor en localStorage para más adelante
    useEffect(() => {
        if (devices) {
            if (devices.length > 0 && devices[0]) {
                localStorage.setItem('CM5DeviceAddress', devices[0].ip);
            } else {
                localStorage.setItem('CM5DeviceAddress', '');
            }
        }
    }, [devices]);

    // Probar que el datáfono en la ip especificada responde
    function testDevice(ip: string): void {
        setTesting(true);

        const config = setupPOSConfig(ip) as any;

        // Hacer la petición, 2 segundos de timeout
        requestAdHoc(
            config.init.method,
            config.init.uri,
            config.init.params() as Record<string, unknown>,
        )
            .then((r: any) => {
                const response = config.init.response(r as POSInitResponse);
                if (response.status === 'OK') {
                    cxt?.showMessage('S', t('config.pos.deviceTestOK'));
                } else {
                    cxt?.showMessage('E', t('config.pos.deviceTestNotFound'));
                }
            })
            .catch(() => {
                cxt?.showMessage('E', t('config.pos.deviceTestError'));
            })
            .finally(() => {
                setTesting(false);
            });
    }

    return (
        <Box>
            {canModify && (
                <SttButton
                    startIcon={<Add />}
                    caption={t('config.pos.addDevice')}
                    color="primary"
                    variant="contained"
                    disabled={false}
                    onClick={() => {
                        setDialogOpen(true);
                    }}
                />
            )}
            {devices && (
                <Box mt={2}>
                    <SttList
                        paper={false}
                        title={t('config.pos.devicesConnected')}
                        onSelect={() => {}}
                        onDelete={
                            canModify
                                ? (ip: string) =>
                                      setDevices(
                                          devices.filter(
                                              (cacharro) => cacharro.ip !== ip,
                                          ),
                                      )
                                : undefined
                        }
                        data={devices.map((item: any) => ({
                            caption: (
                                <Box display="flex" alignItems="center">
                                    <Box mr={3}>{item.ip}</Box>
                                    <SttButton
                                        disabled={testing}
                                        caption={t('Check')}
                                        color="primary"
                                        variant="contained"
                                        onClick={() => testDevice(item.ip)}
                                    />
                                </Box>
                            ),
                            objToSelect: item.ip,
                        }))}
                    />
                </Box>
            )}
            <ConfigurationPosDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onAdd={(ip: string) => {
                    // Se machaca el array con un array de un único valor;
                    setDevices([{ ip }]);
                    setDialogOpen(false);
                }}
            />
        </Box>
    );
}
